import React, {createRef, useContext, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import moment from "moment";
import __ from "lodash";
import MaterialTable, { MTableToolbar } from "@material-table/core";
import {
  TablePagination,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Typography,
  Switch,
  useTheme,
} from "@material-ui/core";
import {
  AddBox,
  ArrowRightAlt,
  Cancel,
  Edit,
  Message,
  Send,
  SwapHoriz,
  ThumbsUpDown
} from "@material-ui/icons";
import { defaultComponents } from '../../../controls/Material-Table-Components';

import {
  MaskedText,
  DatePicker,
  FlightNumber,
  MaskedTime,
  ComboSelect,
  LightTooltip,
  HtmlTooltip,
} from "../../../controls";
import { TermsConditions } from "../../../icons";
import SlotHistoryDialog from "../../../controls/FormDialog";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import { getSeason } from "../../../functions/GetSeason";
import AirportLookup from "../../Airports/components/AirportsLookup";
import AircraftLookup from "../../Aircraft/components/AircraftLookup";
import SlotsValidator from "../validators/SlotsValidator";
import AirportNameTooltip from "../../Airports/components/AirportNameTooltip";

import { useSlots } from "../../../services/slots";
import { useSeasons } from "../../../services/settings";
import Season from "./Season";
import GenerateSlotDialog from "./GenerateSlotDialog";
import SendSlotRequestDialog from "./SendSlotRequestDialog";
import MessageExchangeGrid from "./SlotMessageExchangeGrid";
import UpdateSlotDialog from "./UpdateSlotDialog";
import { Stack } from '@mui/material';
import SystemContext from "../../../SystemContext";
import axios from 'axios';
import { getApiSitePrefix } from '../../../functions/SiteDefaults';

const apiSitePrefix = getApiSitePrefix();

const dateFormat = "DD/MM/YYYY";
const formatChars={
  "U": "[A-Z]",
  "A": "[A-Z0-9]",
  "a": "[A-Za-z0-9]",
  "i": "[A-Za-z0-9\\-]",
  "9": "[0-9]",
  "D": "[0-9.]",
  "-": "-",
  "x": "."
}

const statusValues = [
  {"code":"REQUIRED","value":"Required"},
  {"code":"REQUESTED","value":"Requested"},
  {"code":"RECEIVED","value":"Confirmed"},
  {"code":"REJECTED","value":"Rejected"},
  {"code":"CANCELLED","value":"Cancelled"},
  {"code":"PENDING","value":"Pending"},
  {"code":"OFFER","value":"Offer"},
  {"code":"CONDITIONAL","value":"Conditional"},
];

const serviceValues = [
  {"code":"F","value":"Scheduled"},
  {"code":"H","value":"Charter"},
  {"code":"P","value":"Ferry"},
  {"code":"X","value":"Tech"},
];

const historyButtons = {
  cancel:{icon: "checkCircle", label: "OK", color: "primary"}
};


const SlotList = (_props) => {
  const props = {
    ..._props
  }
  const {classes, eventBus} = props;

  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();

  const [state] = useState({
    canEdit: props.canEdit,
    canManuallyUpdateSlots: props.canManuallyUpdateSlots,
  });
  const [generateSlot, setGenerateSlot] = useState({
    open: false,
    record: {}
  });
  const [sendSlot, setSendSlot] = useState({
    open: false,
    record: {},
    resend: true
  });
  const [slotMessageExchange, setSlotMessageExchange] = useState({
    showNoteDialog: false,
    record: null,
  });
  const [updateSlot, setUpdateSlot] = useState({
    open: false,
    record: {},
    resend: true
  });

  const toggleExplodedView = () => {
    eventBus.trigger('SlotManagement.toggleExplodedView');
  }

  const year = props.year;
  const month = props.month;
  const required = props.required;

  const tableRef = createRef({state: {lastEditingRow: false}});

  const queryClient = useQueryClient();
  const extraKeys = [year, month];
  if (required)
    extraKeys.push(true);

  const slots = useSlots({tableRef, queryClient, extraKeys});
  const seasons = useSeasons({ });


  const refreshList = async () => {
    queryClient.invalidateQueries(["Slots", year, month]);
  }

  const handleUpdateSlotClick = (rowData) => {
    const season = getSeason(seasons.data, rowData.slotPeriodFrom);
    const slotActionCode = rowData.status === "REQUIRED" ? rowData.slotActionCode : "";
    const newSlotMode = ["N","Y","B","V"].includes(rowData.slotActionCode) && !["RECEIVED", "PENDING", "OFFER", "CONDITIONAL"].includes(rowData.status);
    const parentRecord = !newSlotMode ? rowData.parentRecord||rowData : undefined;
    
    const record = {
      ...__.omit(rowData, "processStatus"),
      newSlotMode,
      slotActionCode,
      parentRecord,
      generalInfo: rowData.generalInfo?rowData.generalInfo:"REGARDS MAGMA AVIATION",
      season,
      slotDirection: rowData.slotDirection||'BOTH',
      slotChangePeriodFrom: rowData.slotPeriodFrom,
      slotChangePeriodTo: rowData.slotPeriodTo,
      changeDays: rowData.days,
      slotPeriodFrom: newSlotMode ? rowData.slotPeriodFrom : parentRecord.slotPeriodFrom,
      slotPeriodTo: newSlotMode ? rowData.slotPeriodTo : parentRecord.slotPeriodTo,
      days: newSlotMode ? rowData.days : parentRecord.days,
    }

    setGenerateSlot({...generateSlot, open: true, newSlot: false, record});
  }

  const handleCancelSlotClick = async (rowData) => {
    const {slotUuid} = rowData;
    try {
      SystemData.toggleLoadingProgress(true, "Removing cancelled requirements");
      const response = await axios.put(`${apiSitePrefix}/slots/cancel/${slotUuid}`);
      SystemData.toggleLoadingProgress(false);
      if (!response.data) {
        SystemData.alert("Error", `Could not cancel slot. Server is not responding`);
      } else if (response.data.success) {
        queryClient.invalidateQueries(["Slots", ...extraKeys]);
      } else {
        SystemData.alert("Error", `Could not cancel slot. Server returned ${response.data.error}`);
      }
    } catch (err) {
      SystemData.toggleLoadingProgress(false);
      SystemData.alert("Error", `Could not cancel slot. Server is not responding`);
    }

  }

  const handleNewSlot = () => {
    const season = getSeason(seasons.data, moment().format("YYYY-MM-DD"));

    const record =
      {
        newSlotMode: true,
        status: "REQUIRED",
        slotPeriodFrom: moment.utc().add(1,'day').format("YYYY-MM-DD"),
        slotType: "TURNROUND",
        slotActionCode: "N",
        arrivalService: "H",
        departureService: "H",
        generalInfo: "REGARDS MAGMA AVIATION",
        season: season
      }
    setGenerateSlot({...generateSlot, open: true, newSlot: true, record});
  }

  const handleResendSlot = (rowData) => {
    const season = getSeason(seasons.data, rowData.slotPeriodFrom);

    const parentRecord = rowData.parentRecord||rowData;
    const record = {
      ...rowData,
      newSlotMode: false,
      generalInfo: rowData.generalInfo?rowData.generalInfo:"REGARDS MAGMA AVIATION",
      season,
      parentRecord,
      slotSubject : `${rowData.slotSubject} ${season ? season.seasonCode : ""}`,
      slotDirection: rowData.slotDirection||'BOTH',
      slotChangePeriodFrom: rowData.slotPeriodFrom,
      slotChangePeriodTo: rowData.slotPeriodTo,
      changeDays: rowData.days,
      slotPeriodFrom: parentRecord.slotPeriodFrom,
      slotPeriodTo: parentRecord.slotPeriodTo,
      days: parentRecord.days,
      offsetDays: rowData.offsetDays
    }

    setSendSlot({open: true, request: record, record: record, originalRecord: parentRecord, resend: false})
  }

  const handleManualSlotUpdate = (rowData) => {
    const record =
      {
        ...rowData,
        newSlotMode: false,
        slotActionCode: rowData.slotActionCode,
        coordinatorResponse: ""
      }
    setUpdateSlot({open: true, request: record, record: record, originalRecord: rowData.parentRecord, resend: true})
  }

  const getColumns = (props) => {
    const validatorColumn = ValidatorColumn(SlotsValidator, true);

    const baseColumn = {
      cellStyle: { fontSize:12, padding: "4px 2px" },
      headerStyle: { padding: "4px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
      sorting: false
    };

    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...(ColumnWidth(70, undefined, undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "Airport",
        field: "airportCode",
        initialEditValue: "",
        sorting: true,
        editable: state.canEdit ? "always" : "never",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <LightTooltip title={<AirportNameTooltip airportCode={rowData.airportCode} />}>
            <div>
              {<span> {rowData.airportCode} </span>}
            </div>
          </LightTooltip>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100)),
        title: "Status",
        field: "status",
        editable: state.canEdit ? "always" : "never",
        emptyValue: "",
        initialEditValue: "REQUIRED",
        sorting: true,
        cellStyle: (value, rowData) => {
          const color = ['REQUIRED', 'REJECTED', 'OFFER', ].includes(value) && rowData.processStatus === "ACTIVE"? "red" : ['REQUESTED', 'CONDITIONAL', 'PENDING', ].includes(value) ? "orange" : "";
          const {cellStyle} = ColumnWidth(100);
          return ({...cellStyle, backgroundColor: color, textAlign: "center" });
        },
        render: rowData => (
            <span> { (!rowData.status) ? "" : __.find(statusValues, {code: rowData.status||"REQUESTED"}).value} </span>
        //   <HtmlTooltip rowData={rowData} title={ <ScrMessageView slot={rowData} originalSlot={rowData.parentRecord?rowData.parentRecord:{}} resend/> } >
        //   <span> { (!rowData.status) ? "" : __.find(statusValues, {code: rowData.status||"REQUESTED"}).value} </span>
        // </HtmlTooltip>
      ),
        editComponent: (props) => {
          return (
            <Select
              value={props.value === null?'':props.value}
              onChange={
                (evt, data) => {
                  const comboValue = __.find(statusValues, {value: data.props.value});
                  props.onChange(comboValue.code);
                }
              }
              renderValue={(value) => {
                const comboValue = __.find(statusValues, {code: value});
                const valueToRender = (comboValue === undefined) ? "" : comboValue.value;
                return(`${valueToRender}`);
              }}
            >
              {(statusValues) ?
                statusValues.map((record) => {
                  return (
                    <MenuItem key={record.code} value={record.value}>{record.value}</MenuItem>
                  );
                }) :
                null
              }
            </Select>
          )
        }
      },
      {
        ...baseColumn,
        ...(ColumnWidth(10,)),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(30)),
        title: "",
        field: "conditions",
        editable: "never",
        render: rowData => (
          <>
            {rowData.conditions ? <TermsConditions /> : <></>}
          </>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(40)),
        title: "",
        field: "slotType",
        editable: "never",
        emptyValue: "",
        initialEditValue: "",
        render: rowData => {
          return (
            <>
              {rowData.slotType === "TURNROUND" ? <SwapHoriz /> : <ArrowRightAlt />}
            </>
          );
        },
      },
      {
        ...baseColumn,
        ...(ColumnWidth(125)),
        title: "Last Action",
        field: "slotActionCode",
        editable: "never",
        render: (rowData) => (
          <ComboSelect
            id="slotActionCode"
            label="Slot Request"
            group="SLOTS"
            subGroup="ALL ACTIONS"
            fullWidth
            value={`${rowData.slotActionCode}`}
            displayOnly={true}
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100)),
        title: "Requested On",
        field: "requestedTime",
        editable: "never",
        customFilterAndSearch: (term, rowData) => {
          const searchDate = moment.utc(rowData.requestedTime).startOf('day').format('DD MMM YYYY').toUpperCase();
          const termUpper = `${term}`.toUpperCase();
          return searchDate.indexOf(termUpper) !== -1
        },
        render: rowData => {
          const displayDate = rowData.requestedTime ? moment.utc(rowData.requestedTime).format(dateFormat) : "Not Sent Yet";
          return (<div>{displayDate}</div>);
        },
      },
      {
        ...baseColumn,
        title: "Reference",
        field: "slotReference",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(100)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
        render: rowData =>
          <span> {rowData.slotReference} </span>
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100)),
        title: "Period From",
        field: "slotPeriodFrom",
        customFilterAndSearch: (term, rowData) => {
          const searchDate = moment.utc(rowData.validFrom).startOf('day').format('DD MMM YYYY').toUpperCase();
          const termUpper = `${term}`.toUpperCase();
          return searchDate.indexOf(termUpper) !== -1
        },
        render: rowData => {
          const displayDate = moment.utc(rowData.slotPeriodFrom).format(dateFormat);
          return (<div>{displayDate}</div>);
        },
        editComponent: (props) => (
          <DatePicker
            // minDate={today}
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(100)),
        title: "Period To",
        field: "slotPeriodTo",
        render: rowData => {
          const displayDate = moment.utc(rowData.slotPeriodTo).format(dateFormat);
          return (<div>{displayDate}</div>);
        },
        editComponent: (props) => (
          <DatePicker
            // minDate={today}
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(125)),
        title: "Season",
        field: "slotPeriodTo",
        editable: "never",
        render: rowData => <Season slotFrom={rowData.slotPeriodFrom} />
      },
      {
        ...baseColumn,
        ...(ColumnWidth(5,)),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(55)),
        title: "Days",
        field: "days",
        editable: false,
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"D".repeat(7)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80, 8)),
        title: "Inbound Flight",
        field: "arrivalAirlineCode",
        initialEditValue: "",
        sorting: true,
        customFilterAndSearch: (term, rowData) => {
          const termUpper = `${term}`.toUpperCase();
          if (`${rowData.arrivalAirlineCode} ${rowData.arrivalFlightNo}`.indexOf(termUpper) !== -1) return true;
          if (`${rowData.arrivalAirlineCode}-${rowData.arrivalFlightNo}`.indexOf(termUpper) !== -1) return true;
          if (`${rowData.arrivalAirlineCode}${rowData.arrivalFlightNo}`.indexOf(termUpper) !== -1) return true;
          return false;
        },
        editComponent: (props) => (
          <FlightNumber
            mask={"XX-9999"}
            code={props.rowData.arrivalAirlineCode}
            number={props.rowData.arrivalFlightNo}
            inputProps={defaultEditProps}
            onChange={e => {
              const flightNo = parseInt(e.number);
              let data = { ...props.rowData, arrivalAirlineCode: e.code, arrivalFlightNo: flightNo };
              props.onRowDataChange(data);
            }}
          />
        ),
        render: (props) => (
          <div>
            {props.arrivalAirlineCode}&nbsp;{props.arrivalFlightNo}
          </div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80)),
        title: "Origin Airport",
        field: "originAirportCode",
        initialEditValue: "",
        sorting: true,
        editable: state.canEdit ? "always" : "never",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <LightTooltip title={<AirportNameTooltip airportCode={rowData.originAirportCode} />}>
            <span> {rowData.originAirportCode} </span>
          </LightTooltip>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(70)),
        title: "Arrival Time",
        field: "arrivalTime",
        editComponent: (props) => (
          <MaskedTime
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: rowData => (
          <div>{rowData.arrivalTime?.substr(0,Math.min(rowData.arrivalTime?.length, 5))}</div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80)),
        title: "Departure Time",
        field: "departureTime",
        editComponent: (props) => (
          <MaskedTime
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: rowData => (
          <div>{rowData.departureTime?.substr(0,Math.min(rowData.departureTime?.length, 5))}</div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80)),
        title: "Destination Airport",
        field: "destinationAirportCode",
        initialEditValue: "",
        sorting: true,
        editable: state.canEdit ? "always" : "never",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <LightTooltip title={<AirportNameTooltip airportCode={rowData.destinationAirportCode} />}>
            <span> {rowData.destinationAirportCode} </span>
          </LightTooltip>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80, 8)),
        title: "Outbound Flight",
        field: "departureAirlineCode",
        initialEditValue: "",
        sorting: true,
        customFilterAndSearch: (term, rowData) => {
          const termUpper = `${term}`.toUpperCase();
          if (`${rowData.departureAirlineCode} ${rowData.departureFlightNo}`.indexOf(termUpper) !== -1) return true;
          if (`${rowData.departureAirlineCode}-${rowData.departureFlightNo}`.indexOf(termUpper) !== -1) return true;
          if (`${rowData.departureAirlineCode}${rowData.departureFlightNo}`.indexOf(termUpper) !== -1) return true;
          return false;
        },
        editComponent: (props) => (
          <FlightNumber
            mask={"XX-9999"}
            code={props.rowData.departureAirlineCode}
            number={props.rowData.departureFlightNo}
            inputProps={defaultEditProps}
            onChange={e => {
              const flightNo = parseInt(e.number);
              let data = { ...props.rowData, departureAirlineCode: e.code, departureFlightNo: flightNo };
              props.onRowDataChange(data);
            }}
          />
        ),
        render: (props) => (
          <div>
            {props.departureAirlineCode}&nbsp;{props.departureFlightNo}
          </div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(80, 2, 200)),
        title: "Aircraft",
        field: "aircraftTailNumber",
        editComponent: (props) => (
          <AircraftLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(50, 2, 200)),
        title: "Service",
        field: "arrivalService",
        render: (rowData) => (
          <div>
            <HtmlTooltip title={ <span>{__.find(serviceValues, {code: rowData.arrivalService||"H"}).value} </span> } >
              <span> { rowData.arrivalService} </span>
            </HtmlTooltip>
            <HtmlTooltip title={ <span>{__.find(serviceValues, {code: rowData.departureService||"H"}).value} </span> } >
              <span> { rowData.departureService} </span>
            </HtmlTooltip>
          </div>
        ),
      },
    ];
  };


  return (isAuthenticated &&
    <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
      <MaterialTable
        tableRef={tableRef}
        style={{
          border: "none",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          addRowPosition: "first",
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.tableHeader.backgroundColor,
            color: theme.palette.tableHeader.textColor,
            padding: "2px",
          },
          toolbarButtonAlignment: "right",
          pageSize: 15,
          detailPanelType: "single",
          padding: "dense",
          //overflowY: "scroll",
          // autoExpandTree: (row) => {
          //   const childRows = row?.tableData?.childRows;
          //   if (childRows && Array.isArray(childRows)) {
          //     return Boolean(childRows.find(a => a.status === "REQUIRED"));
          //   }
          // }
        }}
        components={{
          // ...defaultComponents,
          Toolbar: (toolbarProps) => {
            return (
              <div style={{ position: "relative" }}>
                <MTableToolbar {...toolbarProps} />
                {props.canToggleExplodedView?(
                  <div style={{position: "absolute", left: 200, top: 16}}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography>Expand</Typography>
                      <Switch checked={false} onChange={toggleExplodedView}/>
                    </Stack>
                  </div>
                ): (<></>)}
              </div>
            );
          },
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[5, 10, 15]}
            />
          ),
        }}
        actions={[
          {
            icon: 'refresh',
            tooltip: "Refresh List",
            isFreeAction: true,
            onClick: (e) => {
              refreshList();
            }
          },
          {
            hidden: year !== 0 && month !== 0,
            disabled: false,
            icon: () => (
              <IconButton>
                <AddBox/>
              </IconButton>
            ),
            iconProps: { style: { color: theme.palette.primary.main }},
            tooltip: "New Slot",
            isFreeAction: true,
            onClick: handleNewSlot
          },
          (rowData) => (
            {
              hidden: false,
              disabled: false,
              icon: () => (
                <HtmlTooltip
                  rowData={rowData}
                  title={
                    <React.Fragment>
                      <Typography color="inherit">Message Exchange</Typography>
                      <div style={{ whiteSpace: "pre-wrap" }}>
                        {
                          rowData.messageExchange !== null
                            ?
                              rowData.messageExchange.map(exchange => {
                                const date = moment(exchange.messageDate, "YYYY=MM-DD").format("DD/MM/YYYY");
                                const display = `${date} - ${exchange.action}`;
                                return <div><span>{display}</span><br/></div>
                              })
                            : <></>
                        }
                      </div>
                    </React.Fragment>
                  }
                >
                  <IconButton
                    variant="contained" color="primary" size={"small"}
                  >
                    <Message/>
                  </IconButton>
                </HtmlTooltip>),
              iconProps: { style: { color: theme.palette.primary.main }},
              isFreeAction: false,
              onClick: (e, rowData) => {
                if (rowData.messageExchange && Array.isArray(rowData.messageExchange) && rowData.messageExchange.length > 0)
                  setSlotMessageExchange({showNoteDialog: true, rowData: rowData});
              }
            }
          ),
          (rowData) => (
            {
              hidden: year !== 0 && month !== 0,
              disabled: !state.canEdit || (rowData.status !== "REQUESTED" && rowData.status !== "REQUIRED"),
              icon: () => (
                <IconButton color={ state.canEdit && (rowData.status === "REQUESTED" || rowData.status === "REQUIRED") ? "secondary" : "#757575"} >
                  <Send/>
                </IconButton>
              ),
              tooltip: "Re-send Slot Request (only available for 'Requested' statuses)",
              onClick: (e, rowData) => { handleResendSlot (rowData); }
            }
          ),
          (rowData) => (
            {
              hidden: year !== 0 && month !== 0,
              disabled: !state.canManuallyUpdateSlots || rowData.status === "RECEIVED",
              icon: () => (
                <IconButton color={ state.canManuallyUpdateSlots && rowData.status !== "RECEIVED" ? "secondary" : "#757575"} >
                  <ThumbsUpDown/>
                </IconButton>
              ),
              tooltip: `Updated slot status from manual response${!state.canManuallyUpdateSlots ? "- is only available to slot administrators" : rowData.status === "RECEIVED" ? " - is only available for unconfirmed slots" : ""}`,
              onClick: (e, rowData) => { handleManualSlotUpdate(rowData); },
            }
          ),
          (rowData) => (
            {
              hidden: year !== 0 && month !== 0,
              disabled: rowData.status === "REQUESTED" || rowData.status === "OFFER" || rowData.status === "PENDING",
              icon: () => (
                <IconButton variant="contained" size={"small"} color={"secondary"} disabled={!state.canEdit || rowData.status === "REQUESTED" || rowData.status === "OFFER" || rowData.status === "PENDING"}>
                  <Edit/>
                </IconButton>
              ),
              tooltip: "Update Slot",
              //iconProps: { style: { color: theme.palette.primary.main }},
              isFreeAction: false,
              onClick: (e, rowData) => {
                handleUpdateSlotClick(rowData);
              }
            }
          ),
          (rowData) => (
            {
              hidden: year !== 0 && month !== 0,
              disabled: !state.canEdit || (rowData.status !== "REJECTED" && rowData.status !== "REQUIRED"),
              icon: () => (
                <IconButton variant="contained" size={"small"} color={"secondary"} disabled={!state.canEdit || (rowData.status !== "REJECTED" && rowData.status !== "REQUIRED")}>
                  <Cancel/>
                </IconButton>
              ),
              tooltip: "Cancel Slot",
              isFreeAction: false,
              onClick: (e, rowData) => {
                handleCancelSlotClick(rowData);
              }
            }
          ),
        ]}
        title="Slot Management"
        columns={getColumns({canEdit: state.canEdit})}
        data={slots.data}
        editable={{
          isEditable: () => {
            return state.canEdit;
          },
          isDeletable: () => {
            return state.canEdit && year === 0 && month === 0;
          },
          onRowAdd: undefined,
          onRowUpdate: undefined,
          onRowDelete: undefined,
          // onRowDelete: year === 0 && month === 0 ? (oldData) =>
          //   new Promise((resolve, reject) => {
          //     handleDeleteSlot.mutate({oldRecord: oldData, resolve, reject, extraKeys});
          //   }): undefined,
        }}
      />

      {generateSlot.open ?
        <GenerateSlotDialog
          record={generateSlot.record}
          canEdit={state.canEdit}
          close={()=>{setGenerateSlot({open: false, record: {}})}}
          send={(slotRequest, slotRecord, originalRecord)=>{
            setSendSlot({open: true, request: slotRequest, record: slotRecord, originalRecord: originalRecord, resend: false});
            setGenerateSlot({open: false, record: {}});
          }}
          classes={classes}
          dialogStates={{showDialog: true}}
          newSlot={generateSlot.newSlot}
          seasons={seasons.data}
        />
        : <></>
      }
      {updateSlot.open ?
        <UpdateSlotDialog
          request={updateSlot.request}
          record={updateSlot.record}
          originalRecord={updateSlot.originalRecord}
          resend={updateSlot.resend}
          canEdit={state.canEdit}
          close={()=>{setUpdateSlot({open: false, record: {}, resend: true})}}
          classes={classes}
          dialogStates={{showDialog: true, airport: updateSlot.record?.airport}}
        />
        : <></>
      }

      {sendSlot.open ?
        <SendSlotRequestDialog
          request={sendSlot.request}
          record={sendSlot.record}
          originalRecord={sendSlot.originalRecord}
          resend={sendSlot.resend}
          canEdit={state.canEdit}
          close={()=>{setSendSlot({open: false, record: {}, resend: true})}}
          classes={classes}
          dialogStates={{showDialog: true, airport: sendSlot.record?.airport}}
        />
        : <></>
      }

      {slotMessageExchange.showNoteDialog
        ?
        <SlotHistoryDialog
          title="Slot Message Exchange"
          message=""
          open={slotMessageExchange.showNoteDialog}
          record={{notes: slotMessageExchange.rowData.messageExchange}}
          dialogOptions={{maxWidth: "lg", fullWidth: true}}
          buttons={historyButtons}
          movable
          onChange={(result) => {
            setSlotMessageExchange({...slotMessageExchange, showNoteDialog: false, rowData: null});
          }}
        >
          <Grid container checkChildren={true} spacing={3}>
            <Grid item xs={12} checkChildren={true}>
              <MessageExchangeGrid messages={slotMessageExchange.rowData.messageExchange}/>
            </Grid>
          </Grid>
        </SlotHistoryDialog>
        :
        <></>
      }

    </div>
  );


}

export default SlotList;
